import type { PurchaseType } from "components/Shop/PDPPage/types"
import { dispatch } from "@shared/utils.events"
import { MembershipOptionsModal } from "packages/core-shopping/components/PDP/MembershipOptionsModal/MembershipOptionsModal"
import React, { useCallback, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import { PAGE_ELEMENT_VIEWED_EVENT_KEY } from "tracking/hooks/usePageElementEvents"
import type {
  MembershipOption,
  MembershipOptionsV2,
  MembershipPricingOption,
  Price,
  PriceWithMembershipOptions,
  PricingOption,
  ShippingType,
  VariantType,
} from "types"
import { AccordionWithSingleMembershipOption } from "./AccordionWithSingleMembershipOption"
import { ProductActions } from "./ProductActions"
import { useUserContext } from "components/UserProvider"

type Props = {
  variant: VariantType
  shippingInfo?: ShippingType[]
  membershipInBag?: boolean
  hasMembership?: boolean
  isGiftMembership?: boolean
  prices: Price[]
  onPricingSelection?: (price: Price) => void
  onAddToBag?: ({
    includeAccessPass,
    purchaseTypeIdentifier,
    membershipSKU,
    locationTypeDetails,
  }: {
    includeAccessPass: boolean
    purchaseTypeIdentifier: PurchaseType
    membershipSKU: string
    locationTypeDetails?: string
  }) => void
  defaultPriceId: string
  pdpMembershipOptions?: MembershipOptionsV2[]
  defaultMembershipSku?: string
}

const getDefaultMembershipOption = (
  price: PricingOption
): MembershipOption | undefined => {
  if ("memberships" in price) {
    return (price as PriceWithMembershipOptions).memberships.find(
      (membership) => membership.isDefault
    )
  }
  if ("membership" in price) {
    return (price as MembershipPricingOption).membership
  }
}

export const OpenGatePricing: React.FC<Props> = ({
  variant,
  shippingInfo,
  prices,
  onPricingSelection,
  onAddToBag,
  defaultPriceId,
  pdpMembershipOptions,
  defaultMembershipSku,
  membershipInBag = false,
  hasMembership = false,
  isGiftMembership = false,
}) => {
  const defaultPrice = prices.find(
    (priceOption) => priceOption.id === defaultPriceId
  ) as Price

  const [selectedPrice, setSelectedPrice] = useState<Price>(defaultPrice)

  const defaultMembershipForSelectedPrice = getDefaultMembershipOption(
    selectedPrice.price
  )

  const [selectedMembershipSKU, setSelectedMembershipSKU] = useState<
    string | undefined
  >(defaultMembershipForSelectedPrice?.sku)

  const [showMembershipOptions, setShowMembershipOptions] = useState<
    string | null
  >(null)

  const { isMember } = useUserContext()

  const { ref, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
  })

  useEffect(() => {
    if (inView) {
      dispatch(PAGE_ELEMENT_VIEWED_EVENT_KEY, {
        target: "og_pdp_panel",
        location: "og_pdp_panel",
        locationDetails: defaultMembershipSku,
        target2: defaultMembershipSku,
      })
    }
  }, [inView, defaultMembershipSku])

  const onSelectMembership = async (chosenMembership: MembershipOptionsV2) => {
    await onAddToBag?.({
      includeAccessPass: true,
      purchaseTypeIdentifier: "member",
      membershipSKU: chosenMembership.sku,
      locationTypeDetails: "og_membership_selection_panel",
    })
    setShowMembershipOptions(null)
  }

  const onAccordionChange = useCallback(
    (value: string) => {
      const option = prices.filter((priceOption) => priceOption.id === value)[0]
      setSelectedPrice(option)
      setSelectedMembershipSKU(getDefaultMembershipOption(option.price)?.sku)
      onPricingSelection?.(option)
    },
    [setSelectedPrice, prices, onPricingSelection]
  )

  const showAccordionWithSingleMembershipOption =
    !membershipInBag && !hasMembership && !isGiftMembership && !isMember

  return (
    <>
      <section ref={ref} data-testid="og_pdp_panel">
        {showAccordionWithSingleMembershipOption && (
          <AccordionWithSingleMembershipOption
            {...{
              selectedValue: selectedPrice?.id,
              onAccordionChange,
              prices: prices || [],
              setShowMembershipOptions,
              defaultMembershipSku,
            }}
          />
        )}

        <ProductActions
          variant={variant}
          onAddToBag={({
            includeAccessPass,
            purchaseTypeIdentifier,
            membershipOverride,
          }) =>
            onAddToBag?.({
              includeAccessPass,
              purchaseTypeIdentifier,
              membershipSKU: (membershipOverride?.sku ||
                selectedMembershipSKU) as string,
            })
          }
          selectedPriceOption={selectedPrice}
          shippingInfo={shippingInfo}
        />
        {selectedMembershipSKU && (
          <MembershipOptionsModal
            isOpen={!!showMembershipOptions}
            onClose={() => setShowMembershipOptions(null)}
            onSelect={onSelectMembership}
            initialMembershipSku={selectedMembershipSKU}
            memberships={pdpMembershipOptions}
            defaultMembershipSku={defaultMembershipSku}
          />
        )}
      </section>
    </>
  )
}
